// packages/web/src/lib/farSide.ts — A5: EVERY DERIVATION THE FAR SIDE OF THE
// SEAM MAKES, IN ONE PLACE, AS PURE FUNCTIONS.
//
// Sibling of auditionSpread.ts and bound by the same rule, which is worth
// restating rather than cross-referencing:
//
//   THE PACK IS THE VOCABULARY. THIS FILE HOLDS NO COPY OF IT.
//
// No list of engagement states, no list of payment states, no set of
// "terminal" names. The engagement stepper reuses `stepsFor` from
// auditionSpread.ts unchanged — it derives an advancement line from whatever
// lifecycle the server serves, and on `aa_engagement` that yields
// `offered → accepted → contracted → shooting → wrapped` with `cancelled`
// left off. That is the hand-drawn answer, arrived at structurally, and the
// reason there is no second stepper implementation here.
//
// Where this file DOES need to know a terminal state — the money surface must
// exclude payments that are done — it derives that too. See `settledStates`.
//
// ===================================================================
// THE DESIGN LAW OF THIS SIDE OF THE SEAM
// ===================================================================
// BEFORE THE SEAM WE RENDER PROGRESS. AFTER IT WE RENDER OBLIGATIONS.
//
// A role's life before booking is episodic and uncertain: reads happen, and
// the question is *did we hear back*. A stepper, a trail and a pipeline are
// instruments for measuring progress against an unknown end, which is what
// A4 built.
//
// After the seam the life is scheduled and consequential. Days are real,
// amounts are owed, paperwork is either signed or it is not. Each is an
// OBLIGATION WITH A DATE AND A STATE, and the instrument for that is a date
// beside a state that is LOUD WHEN LATE AND QUIET WHEN DONE.
//
// That difference is why the two sides sort in opposite directions, which is
// the single most load-bearing line in this file:
//
//   an audition is a thing that HAPPENED  -> newest first
//   a shoot day is a thing that is COMING -> soonest first
//
// Getting that backwards puts last month's wrap day above tomorrow's 5:40am
// pickup, on the one screen whose entire job is tomorrow.
import { ageInDays, parseDay, RECEIVABLE_BANDS, tallyByAge, type AgedTally } from './aging';
import type { RecordRow, SchemaLifecycle } from './api';

// -------------------------------------------------------------------
// 1. TERMINAL STATES — derived, never named
// -------------------------------------------------------------------

/**
 * The states a record can sit in and never leave: no outgoing transitions.
 *
 * On `aa_payment` this yields `received` and `written_off` — money that has
 * arrived or been given up on — and leaves `expected`, `invoiced` and
 * `disputed` live. On `aa_document` it yields `countersigned` and `void`.
 * Neither name appears anywhere in this file.
 *
 * WHY DERIVED AND NOT LISTED. A pack can add a state in an additive migration
 * (D12), and a hardcoded list of "done" states would then silently treat a
 * new terminal state as live — a payment stuck forever in a chase list that
 * has already been settled. The transition table already knows; ask it.
 *
 * A lifecycle with no transitions at all returns every state, which is
 * correct: nothing can move, so nothing is outstanding.
 */
export function settledStates(lifecycle: SchemaLifecycle | undefined): Set<string> {
  const states = lifecycle?.states ?? [];
  const transitions = lifecycle?.transitions ?? {};
  return new Set(states.filter((s) => (transitions[s] ?? []).length === 0));
}

/**
 * The states the pack says CAN HAPPEN AGAIN — a transition from a state to
 * itself.
 *
 * On `aa_payment` this yields `partially_received` and nothing else: a
 * further part-payment is a real edge (`partially_received ->
 * partially_received`) because a second cheque changes a NUMBER on the same
 * row. On `aa_document` it yields nothing, and deliberately — that lifecycle
 * REFUSES a `redline -> redline` self-loop, because a second redline is a new
 * VERSION of the document rather than an accumulation (see `documentRounds`).
 *
 * WHY THIS IS A DERIVATION AND NOT A NAME. `isSettled` below needs to know
 * that a partially-received payment is not finished, and the one thing this
 * file may never do is hold a copy of the pack's vocabulary. A self-loop is
 * the pack saying, structurally, "more of this is coming" — which is exactly
 * the question, asked of the transition table instead of of a string.
 */
export function recurringStates(lifecycle: SchemaLifecycle | undefined): Set<string> {
  const transitions = lifecycle?.transitions ?? {};
  return new Set(Object.keys(transitions).filter((from) => (transitions[from] ?? []).includes(from)));
}

/**
 * Is this record finished — off the chase, not merely unmovable?
 *
 * ===================================================================
 * TERMINAL IS NOT THE SAME AS SETTLED, AND THE PACK PROVES IT
 * ===================================================================
 * The first version of this function asked only `settledStates().has(state)`,
 * and it was WRONG in a way the unit tests had already recorded without
 * anybody joining it up: on `aa_payment`, `received` HAS an outgoing
 * transition (`received -> disputed`), so it is not terminal — and money that
 * has arrived was therefore being counted as outstanding, listed in the chase
 * and coloured as late. Only `written_off` is terminal in that lifecycle.
 *
 * So "the pack can no longer move it" is the wrong question. The right one is
 * "is there still money to chase", and there are three ways to answer it —
 * none of which names a state:
 *
 *   1. THE STATE IS TERMINAL — nothing can move it, so nothing will. Settled.
 *   2. THE STATE CAN RECUR — the pack declares a self-loop out of it, which
 *      is the pack saying more of this is coming. NOT settled, however much
 *      has already arrived. (P6: this is `partially_received`.)
 *   3. OTHERWISE, AN AMOUNT WAS RECORDED AS RECEIVED — the household has
 *      money in hand and the row is not owed, whatever the state machine
 *      still permits afterwards.
 *
 * ⚑ RULE 2 IS THE FIX FOR A STATED DEFECT, AND ITS ORDER MATTERS. This
 * function used to carry a KNOWN LIMITATION note: a partial payment left the
 * chase entirely under rule 3, so the REMAINDER of a child's money stopped
 * being counted, aged, or chased. The note said it "needs a pack change" —
 * P6 is that change, and rule 2 is how this file reads it without learning
 * the new state's name.
 *
 * ⚠ AND RULE 3 STILL SETTLES A CLOSE-OUT FOR LESS. A household that agreed
 * to take 800 of 1000 ends in `received` with 800 recorded; that state does
 * not recur, so the row is settled and the 200 is not chased forever. The
 * amounts alone could not tell that apart from a partial — only the pack can,
 * and it does.
 */
export function isSettled(record: RecordRow, lifecycle: SchemaLifecycle | undefined): boolean {
  const state = record.lifecycle_state;
  if (state && settledStates(lifecycle).has(state)) return true;
  if (state && recurringStates(lifecycle).has(state)) return false;
  const got = record.fields.received_amount;
  return typeof got === 'number' && Number.isFinite(got);
}

/** What has actually arrived on this row, or 0. `received_amount` is the
 * RUNNING TOTAL in every state the pack declares (its own P6 header), so this
 * is one reading, used by the chase, the "Received" figure and the set-aside
 * alike — three numbers that must never disagree. */
export function receivedOn(row: RecordRow): number {
  const v = row.fields.received_amount;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * ⚑ THE FIGURE ON ONE ROW, OR ITS ABSENCE (2026-09-16, the Day 3–4 walk,
 * item 1).
 *
 * `expectedOn`/`receivedOn` below answer 0 for a missing amount and are RIGHT
 * to: they feed SUMS, and a sum of nothing is zero. A ROW is a different
 * question. Both money screens formatted a row as
 * `received ?? expected ?? 0`, so a payment carrying no amount at all rendered
 * as "$0.00" — the walk found one reading "Other $0.00", staged from a deal
 * memo line that said "per scale, to be confirmed".
 *
 * That is the same rule `setAsideFor` already states in capitals one screen
 * over: NULL IS NOT ZERO. A rendered zero is indistinguishable from a stated
 * zero, and a stated zero about a child's money is a claim nobody printed. The
 * capture lane no longer stages such a row (`figureFields`), and this is the
 * other end: a row that arrived before that, or by hand, says what is true.
 */
export const NO_AMOUNT_RECORDED = 'no amount recorded';

/** The amount to SHOW on one payment row — what arrived if it has, else what
 * is expected, else null. Never a zero standing in for a silence. */
export function rowAmount(row: RecordRow): number | null {
  const received = row.fields.received_amount;
  if (typeof received === 'number' && Number.isFinite(received)) return received;
  const expected = row.fields.expected_amount;
  if (typeof expected === 'number' && Number.isFinite(expected)) return expected;
  return null;
}

/** What was expected on this row, or 0. */
export function expectedOn(row: RecordRow): number {
  const v = row.fields.expected_amount;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * WHAT IS STILL OWED on this row — expected minus what has already arrived.
 *
 * ⚑ THE REMAINDER IS DERIVED, NEVER STORED (the pack's own P6 rule, and the
 * same discipline as aging: "there is no stored age and no job keeping one
 * true"). Before partial payments existed this was simply `expected_amount`,
 * because a row with anything received had already left the chase; now a
 * partially-received row stays IN it, banded by what is left rather than by
 * what the whole invoice was — a household chasing 600 must not read 1,000.
 *
 * FLOORED AT ZERO. An overpayment is a real thing that happens (a
 * miscalculated residual), and letting it subtract from the household's other
 * outstanding money would quietly understate the chase.
 */
export function outstandingOn(row: RecordRow): number {
  return Math.max(0, expectedOn(row) - receivedOn(row));
}

// -------------------------------------------------------------------
// 2. THE DAYS — soonest first, because a shoot day is coming
// -------------------------------------------------------------------

export interface DayOrder {
  /** Today or later, soonest first — what the parent is about to do. */
  upcoming: RecordRow[];
  /** Yesterday or earlier, most recent first — what already happened. */
  past: RecordRow[];
  /** No `shoot_date` at all. Its own group, never folded into either side:
   * a day with no date is not "today" and is not "long ago". */
  undated: RecordRow[];
}

/**
 * Shoot days split around today and ordered for reading, not for storage.
 *
 * THE SPLIT IS THE POINT. A flat chronological list forces the parent to
 * scan for the boundary between done and coming, at 5:40am, which is exactly
 * when scanning fails. Upcoming days ascend (the next one is first, where the
 * eye lands); past days descend (the most recent is first, which is the only
 * one anybody looks back at).
 *
 * `now` is injectable so a test can assert the boundary without waiting for
 * a calendar day to turn.
 */
export function orderShootDays(rows: RecordRow[], now: Date = new Date()): DayOrder {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const upcoming: { row: RecordRow; t: number }[] = [];
  const past: { row: RecordRow; t: number }[] = [];
  const undated: RecordRow[] = [];

  for (const row of rows) {
    const day = parseDay(row.fields.shoot_date);
    if (day === null) {
      undated.push(row);
      continue;
    }
    const t = day.getTime();
    // TODAY COUNTS AS UPCOMING. A shoot day in progress is the most relevant
    // record in the product, and putting it under "past" the moment midnight
    // passes would hide the day the parent is actually living through.
    if (t >= today) upcoming.push({ row, t });
    else past.push({ row, t });
  }

  upcoming.sort((a, b) => a.t - b.t);
  past.sort((a, b) => b.t - a.t);
  return { upcoming: upcoming.map((x) => x.row), past: past.map((x) => x.row), undated };
}

/**
 * The one day that comes to the parent rather than being navigated to —
 * today's, or the next one within `horizonDays`.
 *
 * Null when the next day is further out than the horizon: a call sheet three
 * weeks away is not news, and putting it on Today every morning for three
 * weeks teaches the parent to ignore that part of the screen, which costs
 * more than it ever gains.
 */
export function dayOnTheHorizon(
  rows: RecordRow[],
  horizonDays: number,
  now: Date = new Date()
): RecordRow | null {
  const next = orderShootDays(rows, now).upcoming[0];
  if (!next) return null;
  const days = ageInDays(next.fields.shoot_date, now);
  // ageInDays counts BACKWARDS from the date to now, so a future date is
  // negative. -0 is today, -1 is tomorrow.
  if (days === null) return null;
  return -days <= horizonDays ? next : null;
}

// -------------------------------------------------------------------
// 3. THE MONEY — what is owed, how late, and what is not yet due
// -------------------------------------------------------------------

export interface MoneySplit {
  /** Outstanding and already due: aged into the receivable bands, by the
   * REMAINDER rather than by the invoice (see `outstandingOn`). */
  aged: AgedTally;
  /** Outstanding but NOT YET DUE — lifted out before the tally. */
  notYetDue: { rows: RecordRow[]; total: number; soonest: string | null };
  /** Settled by the pack's own transition table. */
  settled: RecordRow[];
  /** P6 — MONEY IN HAND, across EVERY row: settled ones and the partials
   * still being chased. The screen used to sum `received_amount` over
   * `settled` alone, which was correct only while nothing could be both
   * partly paid and still outstanding. It can now, and a "Received" figure
   * that omitted the 400 already banked on a 1,000 invoice would be the
   * product understating a household's own money. */
  receivedTotal: number;
}

/**
 * The household's money, split the way a parent actually asks about it.
 *
 * ===================================================================
 * WHY FUTURE-DATED MONEY IS LIFTED OUT BEFORE THE TALLY
 * ===================================================================
 * `ageInDays` returns a NEGATIVE age for an `expected_on` in the future, and
 * `RECEIVABLE_BANDS`' first band runs from -Infinity — so a payment due next
 * month lands in "0–30 days" beside one that is genuinely three weeks late.
 *
 * That conflation is tolerable for Rexx the product, where an invoice is
 * raised for work already done and a future due date is unusual. It is WRONG
 * for a household, where half the expected money is post-dated to a shoot
 * that has not happened yet: a parent reading "0–30 days · $2,400" cannot
 * tell whether a cent of it is owed, and the number that is supposed to
 * prompt a phone call instead prompts nothing.
 *
 * So not-yet-due rows come out first and are stated separately, with the date
 * they fall due. THIS IS A DISPLAY SPLIT, NOT A CHANGE TO `aging.ts` — the
 * shared clock keeps its behaviour and its Rexx callers untouched.
 *
 * An absent `expected_on` still goes THROUGH the tally, into its `unknown`
 * bucket. It is not "not yet due" (nobody said so) and not aged (nobody can
 * say). That bucket exists precisely so this function does not have to guess.
 */
export function splitMoney(
  rows: RecordRow[],
  lifecycle: SchemaLifecycle | undefined,
  now: Date = new Date()
): MoneySplit {
  const settled: RecordRow[] = [];
  const notYetDueRows: RecordRow[] = [];
  const chase: RecordRow[] = [];

  for (const row of rows) {
    // ONE RULE, ASKED ONCE. An earlier draft inlined `done.has(state)` here
    // and called isSettled() from the screens — two answers to one question,
    // which is how a row appears in the chase list and renders as done at the
    // same time.
    if (isSettled(row, lifecycle)) {
      settled.push(row);
      continue;
    }
    const days = ageInDays(row.fields.expected_on, now);
    // days < 0 means the due date has not arrived. days === null means nobody
    // recorded one — that goes to the tally's `unknown`, not to here.
    if (days !== null && days < 0) notYetDueRows.push(row);
    else chase.push(row);
  }

  notYetDueRows.sort((a, b) => {
    const ta = parseDay(a.fields.expected_on)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const tb = parseDay(b.fields.expected_on)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return ta - tb;
  });

  const soonestRow = notYetDueRows[0];
  return {
    // ⚑ BANDED BY THE REMAINDER. `outstandingOn`, not `expected_amount` —
    // a partially-received row is aged by what is still owed on it.
    aged: tallyByAge(chase, (r) => r.fields.expected_on, outstandingOn, now, RECEIVABLE_BANDS),
    notYetDue: {
      rows: notYetDueRows,
      total: notYetDueRows.reduce((n, r) => n + outstandingOn(r), 0),
      soonest: typeof soonestRow?.fields.expected_on === 'string' ? soonestRow.fields.expected_on : null,
    },
    settled,
    receivedTotal: rows.reduce((n, r) => n + receivedOn(r), 0),
  };
}

// -------------------------------------------------------------------
// 4. THE SET-ASIDE — the one question a household can be held to
// -------------------------------------------------------------------

export interface SetAside {
  /** What actually arrived for this performer. */
  received: number;
  /** received x set_aside_pct — or NULL when the percentage is not recorded.
   * NEVER ZERO. */
  shouldHaveBeenSetAside: number | null;
  /** The percentage used, so the surface can show its working. */
  pct: number | null;
}

/**
 * What should have been set aside for one performer, from what actually
 * arrived.
 *
 * ===================================================================
 * NULL IS NOT ZERO, AND THIS IS THE WHOLE FUNCTION
 * ===================================================================
 * A performer with no `set_aside_pct` recorded gets `null`, and the surface
 * renders an absence. Returning 0 would put "$0.00 should have been set
 * aside" on a child's page — a precise, confident, false statement about
 * money, produced entirely by a missing field.
 *
 * This is decision `21e9f137`'s receipt-fallback precedent applied to a
 * derivation rather than a stored field: absence must stay absence, because
 * a rendered zero is indistinguishable from a stated zero.
 *
 * AND WHAT IT IS CALLED MATTERS AS MUCH AS WHAT IT COMPUTES. The label is
 * *should have been set aside* — never *set aside*. Decision `cfeaa545`:
 * this product holds no institution binding, no account number and no
 * balance. It cannot know whether the money was actually moved. It answers
 * the one question a household CAN be held to and never pretends to be a
 * bank.
 *
 * `received_amount` is summed, not `expected_amount`: you set aside a share
 * of what came, not of what was hoped for.
 */
export function setAsideFor(payments: RecordRow[], subject: RecordRow | null): SetAside {
  let received = 0;
  for (const p of payments) {
    const v = p.fields.received_amount;
    if (typeof v === 'number' && Number.isFinite(v)) received += v;
  }
  const raw = subject?.fields.set_aside_pct;
  const pct = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  return {
    received,
    pct,
    shouldHaveBeenSetAside: pct === null ? null : received * (pct / 100),
  };
}

// -------------------------------------------------------------------
// 5. THE PAPERWORK — rounds are versions, not repeated states
// -------------------------------------------------------------------

export interface DocumentRound {
  record: RecordRow;
  /** The round's own label, or a positional fallback. */
  label: string;
}

/**
 * A document's rounds, oldest first.
 *
 * THE TRAP THIS AVOIDS. The pack refuses a `redline -> redline` self-loop
 * DELIBERATELY (its own comment: "a second redline is not a second STATE"),
 * because the engine matches transitions by exact {from,to} and has no way to
 * model iteration. Successive rounds are the same state with a new
 * `version_label` and a new file.
 *
 * So a surface that renders state changes shows ONE redline where there were
 * four, and the parent has no way to see that the contract has been round the
 * houses. Rounds are rendered as VERSIONS.
 *
 * An unlabelled round falls back to its position — "Round 3" — rather than
 * rendering blank. The position is real information (it is the third thing
 * that arrived) even when nobody typed a label for it.
 */
export function documentRounds(rows: RecordRow[]): DocumentRound[] {
  const sorted = [...rows].sort((a, b) => {
    const ta = parseDay(a.fields.received_at)?.getTime();
    const tb = parseDay(b.fields.received_at)?.getTime();
    // Undated rounds sink to the bottom in their existing order rather than
    // floating to the top and claiming to be the first version.
    if (ta === undefined && tb === undefined) return 0;
    if (ta === undefined) return 1;
    if (tb === undefined) return -1;
    return ta - tb;
  });
  return sorted.map((record, i) => {
    const raw = record.fields.version_label;
    const label = typeof raw === 'string' && raw.trim() ? raw.trim() : `Round ${i + 1}`;
    return { record, label };
  });
}

// -------------------------------------------------------------------
// 6. THE DEAL LINE — a rate is never rendered alone
// -------------------------------------------------------------------

/**
 * The rate and what it buys, as one string, or null.
 *
 * A BARE NUMBER IS A LIE ABOUT WHAT IT BUYS. "$1,200" beside a booking reads
 * as the whole job; "$1,200 per day" on a nine-day block is nine times that.
 * The pack keeps `rate_basis` as free text precisely because the real ones
 * ("per day", "per episode", "weekly, 5-day") do not fit a picklist, so it is
 * rendered verbatim beside the number and never parsed.
 *
 * A rate with NO basis recorded renders the number followed by nothing — not
 * an invented "flat" or "total", which would be this product asserting a deal
 * term nobody typed.
 */
export function rateLine(fields: Record<string, unknown>, money: (n: number) => string): string | null {
  const rate = fields.rate;
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return null;
  const basis = fields.rate_basis;
  const suffix = typeof basis === 'string' && basis.trim() ? ` ${basis.trim()}` : '';
  return `${money(rate)}${suffix}`;
}

/**
 * The block as one span, or null.
 *
 * Both ends present -> "12 Sep – 30 Sep". One end -> that end alone, prefixed
 * so it does not read as a completed span ("from 12 Sep", "until 30 Sep").
 * Neither -> null, and the surface renders no block at all.
 *
 * CLAUSES ARE BUILT AND JOINED, NEVER PREFIXED. A4's defect 5 was an orphan
 * separator opening a line whose first clause turned out to be absent; every
 * composed line in this file is assembled from a list for that reason.
 */
export function blockSpan(fields: Record<string, unknown>, day: (v: unknown) => string | null): string | null {
  const start = day(fields.block_start);
  const end = day(fields.block_end);
  if (start && end) return `${start} – ${end}`;
  if (start) return `from ${start}`;
  if (end) return `until ${end}`;
  return null;
}

/** Join clauses with the separator, dropping every absent one. The only way
 * a composed line should ever be built here — see `blockSpan`'s note. */
export function clauses(parts: (string | null | undefined)[], sep = ' · '): string {
  return parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0).join(sep);
}
